import React, { useMemo, useState, useEffect } from 'react';
import IconButton from '../../icons/IconButton';
import IconText from '../../icons/IconText';
import { ICON } from '../../../icons/iconIds';
import SelkirkIcon from '../../icons/SelkirkIcon';
import { useAutoSelectSingleFilterMatch } from '../../../utils/qaqc/useAutoSelectSingleFilterMatch';
import {
  ComposedChart,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { 
  transformDuplicatesDriftForChart,
  effectiveWithinRpdDynamic,
  COLORS
} from '../../../utils/qaqc/chartDataTransform';
import {
  computeRpdDomainMax,
  augmentRowsRpdPlot,
  augmentRollingRpdPlot,
  boundsFor11Scatter,
  augment11PairPlot
} from '../../../utils/qaqc/chartOutliers';
import SurroundingSamplesTable from './SurroundingSamplesTable';

const DuplicatesPrecisionChart = ({ 
  data,
  selectedElement = 'Cu',
  selectedSample = null,
  surroundingSamples = null,
  loadingSurrounding = false,
  onElementChange,
  onPointClick,
  onClearSelection,
  onCopyData,
  externalSampleFilter = null,
  height = 500
}) => {
  const [sampleFilter, setSampleFilter] = useState('');

  useEffect(() => {
    if (externalSampleFilter == null || externalSampleFilter === '') return;
    setSampleFilter(String(externalSampleFilter));
  }, [externalSampleFilter]);
  
  // Transform data for chart
  const chartData = useMemo(() => {
    return transformDuplicatesDriftForChart(data, selectedElement);
  }, [data, selectedElement]);
  
  // Filter chart data by sample number, workorder, or hole ID
  const filteredChartData = useMemo(() => {
    if (!sampleFilter) return chartData;
    const term = sampleFilter.toLowerCase();
    return chartData.filter(d => 
      d.origSampleNumber?.toLowerCase().includes(term) ||
      d.dupSampleNumber?.toLowerCase().includes(term) ||
      d.workorderId?.toLowerCase().includes(term) ||
      d.holeId?.toLowerCase().includes(term)
    );
  }, [chartData, sampleFilter]);

  useAutoSelectSingleFilterMatch(sampleFilter, filteredChartData, onPointClick);
  
  // Calculate statistics (use filtered data)
  const stats = useMemo(() => {
    if (filteredChartData.length === 0) return null;
    
    const rpdValues = filteredChartData.map(d => d.rpdPct);
    const meanRPD = rpdValues.reduce((sum, v) => sum + v, 0) / rpdValues.length;
    
    const failures = filteredChartData.filter(d => d.status === 'fail').length;
    const warnings = filteredChartData.filter(d => d.status === 'warning').length;
    const passes = filteredChartData.filter(d => d.status === 'pass').length;
    
    const flagged = filteredChartData.filter(d => d.flag5FailsIn50).length;
    
    // Calculate P95
    const sortedRPD = [...rpdValues].sort((a, b) => a - b);
    const p95Index = Math.floor(sortedRPD.length * 0.95);
    const p95RPD = sortedRPD[p95Index] || 0;
    
    return {
      count: filteredChartData.length,
      meanRPD: meanRPD.toFixed(2),
      p95RPD: p95RPD.toFixed(2),
      failures,
      warnings,
      passes,
      passRate: ((passes / filteredChartData.length) * 100).toFixed(1),
      flagged
    };
  }, [filteredChartData]);

  const rpdPlotData = useMemo(() => {
    if (filteredChartData.length === 0) return { rows: [], yMax: 40 };
    const rpdVals = filteredChartData
      .map((d) => d.rpdPct)
      .filter((v) => v != null && !Number.isNaN(v));
    const yMax = computeRpdDomainMax(rpdVals, 40);
    let rows = augmentRowsRpdPlot(filteredChartData, 'rpdPct', yMax);
    rows = augmentRollingRpdPlot(rows, 'rollingMeanRPD50', yMax);
    return { rows, yMax };
  }, [filteredChartData]);

  const rpdYDomain = useMemo(() => {
    const rows = rpdPlotData.rows;
    if (!rows?.length) return [0, 40];
    const vals = rows
      .map((d) => d.plotRpdPct)
      .filter((v) => typeof v === 'number' && !Number.isNaN(v));
    const cap = rpdPlotData.yMax ?? 40;
    const hi = Math.max(cap, ...(vals.length ? vals : [0]));
    const pad = hi * 0.02 || 1;
    return [0, hi + pad];
  }, [rpdPlotData]);

  const pair11PlotData = useMemo(() => {
    if (filteredChartData.length === 0) return [];
    const o = filteredChartData
      .map((d) => d.origValue)
      .filter((v) => v != null && !Number.isNaN(v));
    const d = filteredChartData
      .map((d) => d.dupValue)
      .filter((v) => v != null && !Number.isNaN(v));
    const { lo, hi } = boundsFor11Scatter(o, d);
    return augment11PairPlot(filteredChartData, lo, hi);
  }, [filteredChartData]);

  const pair11Domain = useMemo(() => {
    if (pair11PlotData.length === 0) return [0, 1];
    const vals = [
      ...pair11PlotData.map((d) => d.plotOrigValue),
      ...pair11PlotData.map((d) => d.plotDupValue)
    ].filter((v) => typeof v === 'number' && !Number.isNaN(v));
    if (!vals.length) return [0, 1];
    const lo = Math.min(...vals);
    const hi = Math.max(...vals);
    const pad = (hi - lo) * 0.04 || 1e-8;
    return [lo - pad, hi + pad];
  }, [pair11PlotData]);
  
  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      
      return (
        <div style={{
          background: '#1f2937',
          border: '1px solid #374151',
          borderRadius: '6px',
          padding: '12px',
          color: '#f9fafb',
          fontSize: '0.875rem',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
          maxWidth: '300px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#06b6d4' }}>
            {data.origSampleNumber} / {data.dupSampleNumber}
          </div>
          <div style={{ fontSize: '0.8125rem', color: '#9ca3af', marginBottom: '8px' }}>
            {data.workorderId} | {data.holeId}
          </div>
          <div style={{ borderTop: '1px solid #374151', paddingTop: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Original Value:</span>
              <span style={{ fontWeight: 'bold' }}>{data.origValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Duplicate Value:</span>
              <span style={{ fontWeight: 'bold' }}>{data.dupValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Mean Value:</span>
              <span style={{ fontWeight: 'bold', color: COLORS.mean }}>{data.meanValue.toFixed(4)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>RPD %:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass
              }}>
                {data.rpdPct.toFixed(2)}%
              </span>
            </div>
            {data.rollingMeanRPD50 !== null && (
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
                <span>Rolling Mean (50):</span>
                <span style={{ fontWeight: 'bold', color: COLORS.value }}>{data.rollingMeanRPD50.toFixed(2)}%</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '4px' }}>
              <span>Grade Bin:</span>
              <span style={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{data.gradeBin}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px' }}>
              <span>Status:</span>
              <span style={{ 
                fontWeight: 'bold',
                color: data.status === 'fail' ? COLORS.fail : data.status === 'warning' ? COLORS.warning : COLORS.pass
              }}>
                {data.withinDynamic ? 'PASS' : 'FAIL'}
              </span>
            </div>
            
            {data.flag5FailsIn50 && (
              <>
                <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px' }}>
                  <div style={{ fontWeight: 'bold', color: '#f59e0b' }}>
                    <IconText icon={ICON.uiWarning} iconSize={14} as="span">5+ failures in last 50 samples</IconText>
                  </div>
                </div>
              </>
            )}
          </div>
          {data.receivedDate && (
            <div style={{ borderTop: '1px solid #374151', marginTop: '8px', paddingTop: '8px', fontSize: '0.75rem', color: '#9ca3af' }}>
              Date: {new Date(data.receivedDate).toLocaleDateString()}
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  
  // Custom dot component for scatter points
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    
    if (!payload || cx === undefined || cy === undefined) return null;
    
    const getColor = () => {
      if (payload.status === 'fail') return COLORS.fail;
      if (payload.status === 'warning') return COLORS.warning;
      return COLORS.pass;
    };
    
    const handleClick = () => {
      if (onPointClick) {
        onPointClick(payload.raw);
      }
    };
    
    const r = payload.flag5FailsIn50 ? 7 : 5;
    const rpdClip = payload.rpdAxisLabel;
    const rpdHigh =
      rpdClip && payload.rpdPct > (payload.plotRpdPct ?? payload.rpdPct);
    const p11 = payload.pair11Label;

    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill={getColor()}
          stroke={payload.flag5FailsIn50 ? '#fbbf24' : 'none'}
          strokeWidth={payload.flag5FailsIn50 ? 2 : 0}
          style={{ cursor: onPointClick ? 'pointer' : 'default' }}
          onClick={handleClick}
          opacity={0.8}
        />
        {payload.flag5FailsIn50 && (
          <text
            x={cx}
            y={p11 || rpdClip ? cy - r - 16 : cy - 12}
            fill="#fbbf24"
            fontSize="12"
            fontWeight="bold"
            textAnchor="middle"
          >
            <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
          </text>
        )}
        {rpdClip && (
          <text
            x={cx}
            y={rpdHigh ? cy + r + 12 : cy - r - 18}
            fill="#e5e7eb"
            fontSize={9}
            textAnchor="middle"
          >
            {rpdClip}
          </text>
        )}
        {p11 && (
          <text
            x={cx}
            y={cy + r + (rpdClip ? 22 : 12)}
            fill="#e5e7eb"
            fontSize={8}
            textAnchor="middle"
          >
            {p11}
          </text>
        )}
      </g>
    );
  };
  
  return (
    <div className="duplicates-precision-chart">
      {/* Filter Controls */}
      <div style={{ 
        padding: '1rem',
        background: '#111827',
        border: '1px solid #374151',
        borderRadius: '8px',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Element Filter */}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Element:</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {['Au', 'Ag', 'Cu'].map(element => (
                <button
                  key={element}
                  onClick={() => onElementChange && onElementChange(element)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    background: selectedElement === element ? '#06b6d4' : '#374151',
                    color: selectedElement === element ? '#0c4a6e' : '#f9fafb',
                    border: 'none',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    fontWeight: selectedElement === element ? '600' : '400',
                    cursor: 'pointer'
                  }}
                >
                  {element}
                </button>
              ))}
            </div>
          </div>
          
          {/* Sample Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flex: 1 }}>
            <label style={{ fontSize: '0.875rem', color: '#9ca3af' }}>Sample Filter:</label>
            <input
              type="text"
              placeholder="Search by sample, workorder, or hole ID..."
              value={sampleFilter}
              onChange={(e) => setSampleFilter(e.target.value)}
              style={{
                padding: '0.25rem 0.75rem',
                background: '#374151',
                color: '#f9fafb',
                border: '1px solid #4b5563',
                borderRadius: '4px',
                fontSize: '0.875rem',
                flex: 1,
                minWidth: '250px'
              }}
            />
            {sampleFilter && (
              <IconButton
                icon={ICON.ddClose}
                onClick={() => setSampleFilter('')}
                ariaLabel="Clear sample filter"
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Statistics Panel */}
      {stats && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
          gap: '0.75rem',
          padding: '1rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          marginBottom: '1rem',
          fontSize: '0.875rem'
        }}>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Total Pairs</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.count}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Mean RPD %</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#06b6d4' }}>{stats.meanRPD}%</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>P95 RPD %</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f59e0b' }}>{stats.p95RPD}%</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.pass }}>{stats.passes}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Warning</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.warning }}>{stats.warnings}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Fail</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: COLORS.fail }}>{stats.failures}</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Pass Rate</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#f9fafb' }}>{stats.passRate}%</div>
          </div>
          <div>
            <div style={{ color: '#9ca3af', marginBottom: '0.25rem' }}>Flagged (5/50)</div>
            <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#fbbf24' }}>{stats.flagged}</div>
          </div>
        </div>
      )}
      
      {/* Chart */}
      {filteredChartData.length === 0 ? (
        <div style={{ 
          padding: '3rem',
          textAlign: 'center',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '8px',
          color: '#9ca3af'
        }}>
          <p>No data available for {selectedElement}.</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            Try selecting a different element{sampleFilter ? ' or adjust your filter' : ''}.
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <ComposedChart 
            data={rpdPlotData.rows} 
            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
            
            <XAxis 
              dataKey="index"
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{
                value: 'Sample Order (by Date)',
                position: 'insideBottom',
                offset: -10,
                style: { fill: COLORS.text, fontSize: 12 }
              }}
            />
            
            <YAxis 
              stroke={COLORS.text}
              tick={{ fill: COLORS.text, fontSize: 11 }}
              label={{ 
                value: 'RPD %', 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: COLORS.text, fontSize: 12 }
              }}
              domain={rpdYDomain}
              allowDataOverflow
            />
            
            {/* Reference lines for common RPD thresholds */}
            <ReferenceLine y={20} stroke={COLORS.warning} strokeDasharray="3 3" strokeWidth={1} strokeOpacity={0.5} label={{ value: '20%', fill: COLORS.warning, fontSize: 10 }} />
            <ReferenceLine y={30} stroke={COLORS.fail} strokeDasharray="3 3" strokeWidth={1} strokeOpacity={0.5} label={{ value: '30%', fill: COLORS.fail, fontSize: 10 }} />
            
            <Tooltip content={<CustomTooltip />} />
            
            {/* Rolling Mean Line */}
            <Line
              type="monotone"
              dataKey="plotRollingMeanRPD50"
              stroke={COLORS.value}
              strokeWidth={3}
              dot={false}
              name="Rolling Mean (50)"
              connectNulls
            />
            
            {/* RPD scatter points */}
            <Scatter
              dataKey="plotRpdPct"
              shape={<CustomDot />}
              name="RPD %"
            />
            
            <Legend 
              wrapperStyle={{ paddingTop: '10px' }}
              content={() => (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: '1.5rem',
                  fontSize: '0.875rem',
                  color: '#9ca3af',
                  flexWrap: 'wrap'
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.pass }} />
                    Pass
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.warning }} />
                    Warning
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.fail }} />
                    Fail
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ width: '16px', height: '3px', background: COLORS.value }} />
                    Rolling Mean
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
                    5+ Fails in 50
                  </span>
                </div>
              )}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      
      {/* Helper text */}
      {onPointClick && filteredChartData.length > 0 && !selectedSample && (
        <div style={{ 
          marginTop: '0.75rem',
          padding: '0.75rem',
          background: '#111827',
          border: '1px solid #374151',
          borderRadius: '6px',
          fontSize: '0.875rem', 
          color: '#9ca3af', 
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '0.5rem'
        }}>
          <SelkirkIcon name={ICON.uiPointerHint} size={20} decorative />
          <span>
            <strong style={{ color: '#06b6d4' }}>Click any data point</strong> to view sample pair details. Rising rolling mean indicates degrading precision.
          </span>
        </div>
      )}
      
      {/* 1:1 Comparison Chart - Original vs Duplicate */}
      {filteredChartData.length > 0 && (
        <div style={{ marginTop: '2rem' }}>
          <h4 style={{ color: '#f9fafb', marginBottom: '0.5rem' }}>
            Original vs Duplicate Values - 1:1 Comparison
          </h4>
          <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '1rem' }}>
            Points close to the 1:1 line indicate good agreement between original and duplicate measurements.
          </p>
          
          <ResponsiveContainer width="100%" height={400}>
            <ComposedChart 
              data={pair11PlotData} 
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.grid} />
              
              <XAxis 
                dataKey="plotOrigValue"
                type="number"
                domain={pair11Domain}
                stroke={COLORS.text}
                tick={{ fill: COLORS.text, fontSize: 11 }}
                label={{ 
                  value: `Original ${selectedElement} Value`, 
                  position: 'insideBottom', 
                  offset: -10,
                  style: { fill: COLORS.text, fontSize: 12 }
                }}
                allowDataOverflow
              />
              
              <YAxis 
                type="number"
                domain={pair11Domain}
                stroke={COLORS.text}
                tick={{ fill: COLORS.text, fontSize: 11 }}
                label={{ 
                  value: `Duplicate ${selectedElement} Value`, 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: COLORS.text, fontSize: 12 }
                }}
                allowDataOverflow
              />
              
              {/* 1:1 Reference Line */}
              <Line
                type="linear"
                dataKey="plotOrigValue"
                stroke="#06b6d4"
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={false}
                name="1:1 Line (Perfect Agreement)"
                isAnimationActive={false}
              />
              
              <Tooltip content={<CustomTooltip />} />
              
              {/* Scatter points */}
              <Scatter
                dataKey="plotDupValue"
                shape={<CustomDot />}
                name="Sample Pairs"
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '10px' }}
                content={() => (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '1.5rem',
                    fontSize: '0.875rem',
                    color: '#9ca3af',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.pass }} />
                      Pass
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.warning }} />
                      Warning
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: COLORS.fail }} />
                      Fail
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ width: '16px', height: '2px', background: '#06b6d4' }} />
                      1:1 Line
                    </span>
                  </div>
                )}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
      
      {/* Selected Sample Details Panel */}
      {selectedSample && (
        <div style={{ 
          marginTop: '1rem',
          background: '#111827',
          border: '2px solid #06b6d4',
          borderRadius: '8px',
          padding: '1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h4 style={{ margin: 0, color: '#06b6d4' }}>
              Selected Sample Pair: {selectedSample.orig_sample_number} / {selectedSample.dup_sample_number}
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={onCopyData}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#06b6d4',
                  color: '#f9fafb',
                  border: '1px solid #0891b2',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <IconText icon={ICON.uiClipboardList} iconSize={16}>Copy Data</IconText>
              </button>
              <button 
                onClick={onClearSelection}
                style={{
                  padding: '0.5rem 1rem',
                  background: '#374151',
                  color: '#f9fafb',
                  border: '1px solid #4b5563',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                <IconText icon={ICON.uiClose} iconSize={16}>Clear Selection</IconText>
              </button>
            </div>
          </div>
          
          <table className="sample-details-table">
            <tbody>
              <tr>
                <th>Workorder ID</th>
                <td>{selectedSample.orig_workorder_id}</td>
                <th>Hole ID</th>
                <td>{selectedSample.hole_id || 'N/A'}</td>
              </tr>
              <tr>
                <th>Parent Sample</th>
                <td>{selectedSample.parent_sample}</td>
                <th>Element</th>
                <td>{selectedSample.element}</td>
              </tr>
              <tr>
                <th>Original Value</th>
                <td>{parseFloat(selectedSample.orig_value).toFixed(5)}</td>
                <th>Duplicate Value</th>
                <td>{parseFloat(selectedSample.dup_value).toFixed(5)}</td>
              </tr>
              <tr>
                <th>Mean Value</th>
                <td>{parseFloat(selectedSample.mean_value).toFixed(5)}</td>
                <th>RPD %</th>
                <td style={{ 
                  color: parseFloat(selectedSample.rpd_pct) > 30 ? '#ef4444' : 
                         parseFloat(selectedSample.rpd_pct) > 20 ? '#f59e0b' : '#10b981'
                }}>
                  {parseFloat(selectedSample.rpd_pct).toFixed(2)}%
                </td>
              </tr>
              <tr>
                <th>Grade Bin</th>
                <td>{selectedSample.grade_bin}</td>
                <th>Rolling Mean RPD (50)</th>
                <td>{selectedSample.rolling_mean_rpd50 ? parseFloat(selectedSample.rolling_mean_rpd50).toFixed(2) + '%' : 'N/A'}</td>
              </tr>
              <tr>
                <th>Bin P50 RPD</th>
                <td>{parseFloat(selectedSample.bin_rpd_p50).toFixed(2)}%</td>
                <th>Bin P95 RPD</th>
                <td>{parseFloat(selectedSample.bin_rpd_p95).toFixed(2)}%</td>
              </tr>
              <tr>
                <th>Within Static Tolerance</th>
                <td style={{ color: selectedSample.within_rpd_tolerance_static ? '#10b981' : '#ef4444' }}>
                  {selectedSample.within_rpd_tolerance_static ? 'YES' : 'NO'}
                </td>
                <th>Within Dynamic Tolerance</th>
                <td style={{ color: effectiveWithinRpdDynamic(selectedSample) ? '#10b981' : '#ef4444' }}>
                  {effectiveWithinRpdDynamic(selectedSample) ? 'YES' : 'NO'}
                </td>
              </tr>
              <tr>
                <th>Received Date</th>
                <td>{selectedSample.received_date ? new Date(selectedSample.received_date).toLocaleString() : 'N/A'}</td>
                <th>Lab Code</th>
                <td>{selectedSample.lab_code || 'N/A'}</td>
              </tr>
              {selectedSample.flag_5_fails_in_last_50 && (
                <tr>
                  <th>Performance Flag</th>
                  <td colSpan="3" style={{ color: '#f59e0b' }}>
                    <IconText icon={ICON.uiWarning} iconSize={14} as="span">5 or more failures in last 50 samples</IconText> - Precision degradation detected
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          
          <SurroundingSamplesTable 
            data={surroundingSamples}
            loading={loadingSurrounding}
            selectedSample={selectedSample}
          />
        </div>
      )}
    </div>
  );
};

export default DuplicatesPrecisionChart;
